/**
 * Helpers for parsing incident payloads into structured investigation sections.
 */
import type { AmsIncident } from '../services/api';
import type { DashboardResponse, EvidenceRow } from '../types/dashboard';
import { parseRcaInvestigation } from './rcaInvestigation';
import { decodeMimeText } from './mimeText';
import {
  SIGNAL_KIND_META,
  classifyIncidentSignal,
  type IncidentSignalKind,
} from './operateWorkers';

const CHANNEL_ORDER: IncidentSignalKind[] = ['email', 'log', 'edr', 'runtime', 'other'];

/** Group incidents by signal channel for the Incident rail (kept out of .tsx for HMR). */
export function groupIncidentsBySignal(incidents: AmsIncident[]) {
  const groups: {
    kind: IncidentSignalKind;
    label: string;
    emoji: string;
    incidents: AmsIncident[];
  }[] = [];
  for (const kind of CHANNEL_ORDER) {
    const list = incidents.filter((i) => classifyIncidentSignal(i.source, i.raw_payload) === kind);
    // Always surface Email / Logs / EDR so the rail shows channel separation.
    if (!list.length && !['email', 'log', 'edr'].includes(kind)) continue;
    groups.push({
      kind,
      label: SIGNAL_KIND_META[kind].label,
      emoji: SIGNAL_KIND_META[kind].emoji,
      incidents: list,
    });
  }
  return groups;
}

export interface ParsedEmail {
  from?: string;
  subject?: string;
  body?: string;
  receivedAt?: string;
  attachments?: string[];
}

export interface ParsedLogLine {
  id: string;
  timestamp?: string;
  level?: string;
  message: string;
  source?: string;
}

export interface ParsedEdrEvent {
  id: string;
  name: string;
  status?: string;
  detail?: string;
}

export interface IncidentRcaSummary {
  headline?: string;
  known?: string;
  likely?: string;
  unknown?: string;
  confidence?: string;
  confidenceScore?: number;
  correlation?: string;
  items: string[];
}

export interface ParsedIncidentInvestigation {
  signalKind: IncidentSignalKind;
  email: ParsedEmail | null;
  logs: ParsedLogLine[];
  edr: ParsedEdrEvent[];
  evidence: EvidenceRow[];
  artifacts: DashboardResponse['root_cause_analysis']['repository_correlation']['artifacts'];
  rca: IncidentRcaSummary;
  hasInvestigationGraph: boolean;
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

export function parseIncidentEmail(inc: AmsIncident): ParsedEmail | null {
  const payload = inc.raw_payload ?? {};
  const subject = pickString(payload, 'subject', 'Subject', 'email_subject');
  const body = pickString(payload, 'body', 'Body', 'text', 'html', 'plain');
  const from = pickString(payload, 'from', 'From', 'sender');
  const receivedAt = pickString(payload, 'received_at', 'date', 'Date');
  const attachmentsRaw = payload.attachments ?? payload.Attachments;
  const attachments = Array.isArray(attachmentsRaw)
    ? attachmentsRaw.map((a) => (typeof a === 'string' ? a : String(a))).filter(Boolean)
    : undefined;

  if (!subject && !body && !from && !inc.source?.toLowerCase().includes('email')) {
    if (classifyIncidentSignal(inc.source, payload) !== 'email') return null;
  }

  return {
    from: from ? decodeMimeText(from) : undefined,
    subject: decodeMimeText(subject || inc.title),
    body: body || inc.summary || '',
    receivedAt: receivedAt || inc.created_at,
    attachments,
  };
}

function evidenceToLogs(evidence: EvidenceRow[]): ParsedLogLine[] {
  return evidence
    .filter((e) => {
      const cat = (e.category || '').toLowerCase();
      const src = (e.source || '').toLowerCase();
      return cat.includes('log') || src.includes('log') || cat.includes('runtime');
    })
    .map((e, i) => ({
      id: `log-ev-${i}`,
      message: e.value || e.label,
      source: e.source,
      level: e.category,
    }));
}

function checksToEdr(
  checks: AmsIncident['verification_checks'],
  runtime?: AmsIncident['runtime_verification'],
): ParsedEdrEvent[] {
  const out: ParsedEdrEvent[] = [];
  for (const c of checks ?? []) {
    const src = (c.source || '').toLowerCase();
    const label = (c.label || '').toLowerCase();
    if (src.includes('edr') || src.includes('endpoint') || label.includes('health') || label.includes('runtime')) {
      out.push({ id: c.id, name: c.label, status: c.status, detail: c.detail });
    }
  }
  for (const [i, h] of (runtime?.health_checks ?? []).entries()) {
    out.push({
      id: `hc-${i}`,
      name: h.name || `Health check ${i + 1}`,
      status: h.status,
      detail: h.detail,
    });
  }
  return out;
}

export function buildIncidentRca(
  inc: AmsIncident,
  dashboard?: DashboardResponse | null,
): IncidentRcaSummary {
  const parsed = parseRcaInvestigation(inc.rca);
  const dashRca = dashboard?.root_cause_analysis;
  const items: string[] = [];

  for (const item of parsed.coreItems) {
    const text =
      pickString(item as Record<string, unknown>, 'finding', 'cause', 'summary', 'description') ||
      JSON.stringify(item).slice(0, 200);
    if (text) items.push(text);
  }
  for (const wf of parsed.workerFindings) {
    const text = pickString(wf as Record<string, unknown>, 'finding', 'summary', 'detail');
    if (text) items.push(text);
  }

  return {
    headline: dashRca?.likely_root_cause || items[0],
    known: dashRca?.known,
    likely: dashRca?.likely_root_cause,
    unknown: dashRca?.unknown,
    confidence: dashRca?.confidence,
    confidenceScore: dashRca?.confidence_score,
    correlation: dashRca?.repository_correlation?.summary,
    items,
  };
}

export function parseIncidentInvestigation(
  inc: AmsIncident,
  dashboard?: DashboardResponse | null,
): ParsedIncidentInvestigation {
  const payload = inc.raw_payload ?? {};
  const signalKind = classifyIncidentSignal(inc.source, payload);
  const evidence = dashboard?.root_cause_analysis?.supporting_evidence ?? [];
  const artifacts = dashboard?.root_cause_analysis?.repository_correlation?.artifacts ?? [];

  const logs: ParsedLogLine[] = [...evidenceToLogs(evidence)];

  const logLines = payload.log_lines ?? payload.lines ?? payload.messages;
  if (Array.isArray(logLines)) {
    for (const [i, line] of logLines.entries()) {
      if (typeof line === 'string') {
        logs.push({ id: `raw-log-${i}`, message: line, source: inc.source });
      } else if (line && typeof line === 'object') {
        const o = line as Record<string, unknown>;
        logs.push({
          id: `raw-log-${i}`,
          message: pickString(o, 'message', 'text', 'line') || JSON.stringify(o),
          timestamp: pickString(o, 'timestamp', 'time'),
          level: pickString(o, 'level', 'severity'),
          source: inc.source,
        });
      }
    }
  }

  let edr = checksToEdr(inc.verification_checks, inc.runtime_verification);
  if (signalKind === 'edr' && edr.length === 0) {
    edr = evidence
      .filter(
        (e) =>
          (e.source || '').toLowerCase().includes('edr') ||
          (e.category || '').toLowerCase().includes('endpoint'),
      )
      .map((e, i) => ({ id: `edr-ev-${i}`, name: e.label, status: e.category, detail: e.value }));
  }

  return {
    signalKind,
    email: parseIncidentEmail(inc),
    logs,
    edr,
    evidence,
    artifacts,
    rca: buildIncidentRca(inc, dashboard),
    hasInvestigationGraph: Boolean(parseRcaInvestigation(inc.rca).graph),
  };
}
